import {
  WSButton,
  WSCard,
  WSDivider,
  WSFlexBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  Currency,
  ProfitAndLoss,
  TaxEstimate
} from "@wingspanhq/wingspan-api-sdk";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React, { useState } from "react";

const money = (value?: string | number) =>
  formatMoney(Number(value ?? 0), Currency.USD);

const SummaryRow: React.FC<{
  label: string;
  value: string;
  bold?: boolean;
}> = props => (
  <WSFlexBox.CenterY justify="space-between" wrap="nowrap">
    <WSText
      color={props.bold ? "gray700" : "gray600"}
      weight={props.bold ? "medium" : undefined}
    >
      {props.label}
    </WSText>
    <WSText weight={props.bold ? "medium" : undefined} tabularNums>
      {props.value}
    </WSText>
  </WSFlexBox.CenterY>
);

const BreakdownRow: React.FC<{
  label: string;
  rate: string;
  value: string;
  bold?: boolean;
}> = props => (
  <WSFlexBox.CenterY justify="space-between" wrap="nowrap">
    <WSText color="gray600" weight={props.bold ? "medium" : undefined}>
      {props.label}
    </WSText>
    <WSFlexBox.CenterY wrap="nowrap">
      <WSText mr="XL" color="gray500" tabularNums>
        {props.rate}
      </WSText>
      <WSText weight={props.bold ? "medium" : undefined} tabularNums>
        {props.value}
      </WSText>
    </WSFlexBox.CenterY>
  </WSFlexBox.CenterY>
);

type Props = {
  data: ProfitAndLoss;
  taxEstimate?: TaxEstimate;
  taxEstimateLoading?: boolean;
  year: number;
  onViewAllTransactions: () => void;
};

export const ProfitAndLossStatement: React.FC<Props> = props => {
  const [showBreakdown, setShowBreakdown] = useState(false);

  const profit = Number(props.data.netProfit ?? 0);
  const estimate = props.taxEstimate?.estimatedTax;
  const totalTax = Number(estimate?.total ?? 0);
  const estimatedEarnings = profit - totalTax;

  const rate = (amount?: string | number) => {
    if (profit <= 0) {
      return "—";
    }
    return `${((Number(amount ?? 0) / profit) * 100).toFixed(2)}%`;
  };

  // The overall effective rate is authoritative from the estimate (a percentage,
  // e.g. 24.5), rather than derived as total-tax ÷ profit like the per-line rates.
  const effectiveTaxRate = props.taxEstimate?.effectiveTaxRate;
  const effectiveRateLabel =
    effectiveTaxRate === undefined ? "—" : `${effectiveTaxRate.toFixed(2)}%`;

  return (
    <WSCard>
      <WSList gap="M">
        <SummaryRow label="Income" value={money(props.data.totalIncome)} />
        <SummaryRow
          label="Deductible expenses"
          value={money(props.data.totalExpenses)}
        />

        <WSDivider />

        <SummaryRow
          label="Profit before taxes"
          value={money(props.data.netProfit)}
          bold
        />

        <WSFlexBox.CenterY justify="space-between" wrap="nowrap">
          <WSButton.Link
            rightIcon={showBreakdown ? "chevron-up" : "chevron-down"}
            onClick={() => setShowBreakdown(show => !show)}
          >
            Estimated taxes ({props.year})
          </WSButton.Link>
          <WSText shimmer={props.taxEstimateLoading} tabularNums>
            {money(estimate?.total)}
          </WSText>
        </WSFlexBox.CenterY>

        {showBreakdown ? (
          <WSCard>
            <WSList gap="S">
              <BreakdownRow
                label="Self employment tax"
                rate={rate(estimate?.selfEmployment)}
                value={money(estimate?.selfEmployment)}
              />
              <BreakdownRow
                label="Federal income tax"
                rate={rate(estimate?.federal)}
                value={money(estimate?.federal)}
              />
              <BreakdownRow
                label="State income tax"
                rate={rate(estimate?.state)}
                value={money(estimate?.state)}
              />
              <WSDivider />
              <BreakdownRow
                label={`Estimated taxes (${props.year})`}
                rate={effectiveRateLabel}
                value={money(estimate?.total)}
                bold
              />
            </WSList>
          </WSCard>
        ) : null}

        <WSDivider />

        <SummaryRow
          label="Estimated earnings"
          value={money(estimatedEarnings)}
          bold
        />

        <WSButton.Link
          rightIcon="arrow-right"
          onClick={props.onViewAllTransactions}
        >
          View all transactions
        </WSButton.Link>
      </WSList>
    </WSCard>
  );
};
